import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useCoverEditor } from '../../CoverEditorContext';
import { coverEditorMessages } from '../../coverEditorMessages';
import { InspectorRow } from '../../inspector/controls/InspectorRow';
import { Segmented } from '../../inspector/controls/Segmented';
import { Slider } from '../../inspector/controls/Slider';
import { Swatches } from '../../inspector/controls/Swatches';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

type ColourKind = 'colour' | 'gradient';

/**
 * The Colour tab (design cover-editor c5): one colour or a gradient of two colours as the
 * background, from the colours of the cover or any colour.
 */
const ColourTabComponent: React.FC = () => {
  const intl = useIntl();
  const { state, dispatch, readOnly } = useCoverEditor();
  const { background, palette } = state.doc;
  const first = palette[0] ?? '#1f5e4a';
  const last = palette[palette.length - 1] ?? '#e9f5c8';
  const kind: ColourKind = background.kind === 'gradient' ? 'gradient' : 'colour';
  const gradient = useMemo(
    () =>
      background.kind === 'gradient' ? background : { kind: 'gradient' as const, from: first, to: last, angleDeg: 160 },
    [background, first, last]
  );
  const colour = background.kind === 'colour' ? background.colour : first;
  const kinds = useMemo(
    () => [
      { value: 'colour' as const, label: intl.formatMessage(coverEditorMessages.colourSolid) },
      { value: 'gradient' as const, label: intl.formatMessage(coverEditorMessages.colourGradient) },
    ],
    [intl]
  );
  const setKind = useCallback(
    (next: ColourKind) =>
      dispatch({ type: 'setBackground', background: next === 'colour' ? { kind: 'colour', colour } : gradient }),
    [colour, dispatch, gradient]
  );
  const setGradient = useCallback(
    (change: { from?: string; to?: string; angleDeg?: number }, field: string) =>
      dispatch({ type: 'setBackground', background: { ...gradient, ...change }, coalesce: `background:${field}` }),
    [dispatch, gradient]
  );
  const isShown = background.kind !== 'picture';

  return (
    <>
      <Segmented
        label={intl.formatMessage(coverEditorMessages.colourKind)}
        options={kinds}
        value={kind}
        disabled={readOnly}
        onChange={setKind}
      />
      {kind === 'colour' ? (
        <div className={styles.inspectorGrid}>
          <InspectorRow label={intl.formatMessage(coverEditorMessages.colourSolid)}>
            <Swatches
              label={intl.formatMessage(coverEditorMessages.colourSolid)}
              palette={palette}
              value={colour}
              disabled={readOnly}
              onChange={(value) =>
                dispatch({
                  type: 'setBackground',
                  background: { kind: 'colour', colour: value },
                  coalesce: 'background:colour',
                })
              }
            />
          </InspectorRow>
        </div>
      ) : (
        <div className={styles.inspectorGrid}>
          <InspectorRow label={intl.formatMessage(coverEditorMessages.colourFrom)}>
            <Swatches
              label={intl.formatMessage(coverEditorMessages.colourFrom)}
              palette={palette}
              value={gradient.from}
              disabled={readOnly}
              onChange={(from) => setGradient({ from }, 'from')}
            />
          </InspectorRow>
          <InspectorRow label={intl.formatMessage(coverEditorMessages.colourTo)}>
            <Swatches
              label={intl.formatMessage(coverEditorMessages.colourTo)}
              palette={palette}
              value={gradient.to}
              disabled={readOnly}
              onChange={(to) => setGradient({ to }, 'to')}
            />
          </InspectorRow>
          <InspectorRow label={intl.formatMessage(coverEditorMessages.colourAngle)}>
            <Slider
              label={intl.formatMessage(coverEditorMessages.colourAngle)}
              value={Math.round(gradient.angleDeg)}
              min={0}
              max={360}
              step={5}
              display={`${Math.round(gradient.angleDeg)}°`}
              disabled={readOnly}
              onChange={(angleDeg) => setGradient({ angleDeg }, 'angle')}
            />
          </InspectorRow>
        </div>
      )}
      <span className={styles.hint}>
        {intl.formatMessage(isShown ? coverEditorMessages.colourHint : coverEditorMessages.colourReplacesPicture)}
      </span>
    </>
  );
};

export const ColourTab = TypedMemo(ColourTabComponent);
